export const colors = {
  // Backgrounds
  base: 'var(--color-base)',
  surface: 'var(--color-surface)',
  surfaceElevated: 'var(--color-surface-elevated)',

  // Text
  text: 'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textSubtle: 'var(--color-text-subtle)',

  // Brand (violet — identity, logo, nav active, agent/AI context)
  brand: 'var(--color-brand)',
  brandDim: 'var(--color-brand-dim)',
  brandBorder: 'var(--color-brand-border)',
  brandText: 'var(--color-brand-text)',

  // Interactive (blue — buttons, focus rings, selected borders, links)
  interactive: 'var(--color-interactive)',
  interactiveDim: 'var(--color-interactive-dim)',
  interactiveBorder: 'var(--color-interactive-border)',

  // Legacy aliases (kept for backward compat)
  primary: 'var(--color-primary, #1A3C6E)',
  primaryInteractive: 'var(--color-primary-interactive, #2563EB)',
  accent: 'var(--color-accent, #D97706)',

  // Semantic
  warn: 'var(--color-warn)',
  warnDim: 'var(--color-warn-dim)',
  warnBorder: 'var(--color-warn-border)',
  statusGreen: 'var(--color-status-green)',
  statusGreenDim: 'var(--color-status-green-dim)',
  statusGreenBorder: 'var(--color-status-green-border)',
  statusRed: 'var(--color-status-red)',
  statusRedDim: 'var(--color-status-red-dim)',
  statusRedBorder: 'var(--color-status-red-border)',
  statusYellow: 'var(--color-status-yellow)',

  // Borders
  border: 'var(--color-border)',
  borderEmphasis: 'var(--color-border-emphasis)',

  // Nav (dark shell — always dark regardless of theme)
  navBg: '#080E18',
  navBorder: '#131C2E',
  navText: '#64748B',
  navTextActive: '#E2E8F0',
  navActiveBg: '#161D2B',
} as const;

export const typography = {
  fontInterface: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontMono: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
  sizeXs: '11px',
  sizeSm: '12px',
  sizeBase: '13px',
  sizeMd: '14px',
  sizeLg: '16px',
  sizeXl: '18px',
} as const;

export const spacing = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
} as const;

export const radius = {
  card: '4px',
  chip: '2px',
  none: '0px',
} as const;

export const transitions = {
  panel: '120ms ease-out',
  hover: '80ms ease-out',
} as const;

export const layout = {
  navCollapsed: '56px',
  navExpanded: '220px',
  tableRowHeight: '36px',
} as const;

export const nodeColors: Record<string, string> = {
  SOURCE: '#1A3C6E',
  OBJECT_SOURCE: '#0D6E5A',
  FILTER: '#7C3AED',
  MAP: '#0891B2',
  CAST: '#059669',
  ENRICH: '#D97706',
  FLATTEN: '#DC2626',
  CHUNK_TEXT: '#9333EA',
  EMBED_TEXT: '#0F766E',
  UNPIVOT: '#BE185D',
  DEDUPE: '#64748B',
  GROUP_BY: '#A16207',
  EXPRESSION: '#0369A1',
  CODE: '#334155',
  JOIN: '#4F46E5',
  VALIDATE: '#0D9488',
  SINK_OBJECT: '#1A3C6E',
  SINK_DATASET: '#0E7490',
  SINK_EVENT: '#059669',
  AGENT_RUN: '#7C3AED',
  LLM_CLASSIFY: '#DC2626',
  JUDGE_CLASSIFY: '#0D9488',
  ATTACHMENT_PARSE: '#0891B2',
  ATTACHMENT_EXTRACT: '#B45309',
  ROLLUP_AGGREGATE: '#0EA5E9',
  VISION_EXTRACT: '#E1306C',
};

/** Connector category tints: palettes.css holds the light values (unchanged)
 *  and their dark variants. */
export const categoryColors: Record<string, { bg: string; text: string }> = {
  REST: { bg: 'var(--cat-rest-bg)', text: 'var(--cat-rest-text)' },
  GraphQL: { bg: 'var(--cat-graphql-bg)', text: 'var(--cat-graphql-text)' },
  Stream: { bg: 'var(--cat-stream-bg)', text: 'var(--cat-stream-text)' },
  ERP: { bg: 'var(--cat-erp-bg)', text: 'var(--cat-erp-text)' },
  CRM: { bg: 'var(--cat-crm-bg)', text: 'var(--cat-crm-text)' },
  DB: { bg: 'var(--cat-db-bg)', text: 'var(--cat-db-text)' },
  File: { bg: 'var(--cat-file-bg)', text: 'var(--cat-file-text)' },
  Doc: { bg: 'var(--cat-doc-bg)', text: 'var(--cat-doc-text)' },
  HTTP: { bg: 'var(--cat-http-bg)', text: 'var(--cat-http-text)' },
  DW: { bg: 'var(--cat-dw-bg)', text: 'var(--cat-dw-text)' },
};

/** True when <html> carries data-ui="v2" (tenant flag ui_v2; App.tsx ThemeSync
 *  sets it). Read at the moment a color is looked up, so module-level imports
 *  of the palettes below follow the flag without a hook. */
export function uiV2Active(): boolean {
  return typeof document !== 'undefined' && document.documentElement.getAttribute('data-ui') === 'v2';
}

/** A read-only array whose items come from `v1` or `v2` depending on the
 *  interface flag, looked up on every access (index, map, spread...). */
function byInterface(v1: readonly string[], v2: readonly string[]): readonly string[] {
  const out: string[] = [];
  v1.forEach((_, i) => {
    Object.defineProperty(out, i, { enumerable: true, get: () => (uiV2Active() ? v2[i] : v1[i]) });
  });
  return Object.freeze(out);
}

const CHART_PALETTE_V1 = [
  '#1A3C6E', // primary navy
  '#7C3AED', // brand violet
  '#059669', // green
  '#D97706', // amber/accent
  '#DC2626', // red
  '#0891B2', // cyan
  '#2563EB', // interactive blue
  '#DB2777', // pink
  '#65A30D', // lime
  '#6366F1', // indigo
  '#0D9488', // teal
  '#F97316', // orange
] as const;

/** Blueprint data-visualization palette (Palantir), muted for dense UIs. */
const CHART_PALETTE_V2 = [
  '#2D72D2', // blue
  '#29A634', // green
  '#D1980B', // gold
  '#D33D17', // vermilion
  '#7961DB', // violet
  '#00A396', // turquoise
  '#DB2C6F', // rose
  '#8EB125', // lime
  '#946638', // sepia
  '#147EB3', // cerulean
  '#C87619', // orange
  '#634DBF', // indigo
] as const;

const LINE_PALETTE_V1 = [
  '#3B82F6', // blue
  '#F59E0B', // amber
  '#10B981', // emerald
  '#8B5CF6', // violet
  '#EF4444', // red
  '#06B6D4', // cyan
  '#EC4899', // pink
  '#84CC16', // lime
  '#6366F1', // indigo
  '#F97316', // orange
  '#14B8A6', // teal
  '#A855F7', // purple
] as const;

/** The same Blueprint hues, ordered so neighbouring lines differ most. */
const LINE_PALETTE_V2 = [
  '#2D72D2', // blue
  '#D1980B', // gold
  '#29A634', // green
  '#7961DB', // violet
  '#D33D17', // vermilion
  '#00A396', // turquoise
  '#DB2C6F', // rose
  '#8EB125', // lime
  '#147EB3', // cerulean
  '#C87619', // orange
  '#946638', // sepia
  '#634DBF', // indigo
] as const;

/** Ordered palette for chart series (bars, pie slices, area fills, lines).
 *  Derived from brand, interactive, and semantic tokens for visual cohesion;
 *  the Blueprint palette with the new interface (ui_v2). */
export const chartPalette = byInterface(CHART_PALETTE_V1, CHART_PALETTE_V2);

/** Brighter palette tuned for thin strokes on white — used by line/area
 *  charts where the muted chartPalette (built for filled bars/slices)
 *  reads as murky. Ordering puts maximally-distinguishable hues first.
 *  The Blueprint palette with the new interface (ui_v2). */
export const linePalette = byInterface(LINE_PALETTE_V1, LINE_PALETTE_V2);

export type ColorToken = keyof typeof colors;
export type SpacingToken = keyof typeof spacing;
